import React, { useEffect, useContext, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Alert, Button, Spinner } from 'react-bootstrap';
import AuthContext, { decodeJwtPayload } from '../context/AuthContext';

// 네이버 OAuth 콜백 후 백엔드가 token=JWT 쿼리로 리다이렉트 → 여기서 토큰 저장 + 워크보드 이동
// 에러 케이스:
// - error=email_already_used : 이미 일반 가입된 이메일
// - error=signup_failed      : 매장 생성 중 오류
// - error=token_exchange_failed / profile_fetch_failed / oauth_error 등
const ERROR_MESSAGES = {
    naver_denied: '네이버 로그인을 취소했습니다.',
    invalid_callback: '잘못된 콜백 요청입니다.',
    invalid_state: '인증 상태가 올바르지 않습니다.',
    token_exchange_failed: '네이버 토큰 교환에 실패했습니다.',
    profile_fetch_failed: '네이버 프로필 조회에 실패했습니다.',
    account_disabled: '비활성화된 계정입니다. 관리자에게 문의해주세요.',
    email_already_used: '이미 일반 가입된 이메일입니다. 일반 로그인을 이용해주세요.',
    signup_failed: '가입 처리 중 오류가 발생했습니다.',
    oauth_error: '인증 처리 중 오류가 발생했습니다.',
};

const OAuthCompletePage = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const { loginWithToken } = useContext(AuthContext);
    const [error, setError] = useState('');

    useEffect(() => {
        const token = searchParams.get('token');
        const pendingToken = searchParams.get('pending_token');
        const errorCode = searchParams.get('error');

        if (errorCode) {
            setError(ERROR_MESSAGES[errorCode] || '인증에 실패했습니다.');
            return;
        }

        // 신규 OAuth 가입: pending_token에서 hint 추출 후 추가 정보 입력 페이지로 이동
        if (pendingToken) {
            let hints = {};
            try {
                const payload = decodeJwtPayload(pendingToken);
                hints = {
                    shopNameHint: payload.shopNameHint,
                    ownerNameHint: payload.ownerNameHint,
                    naverName: payload.naverName,
                    naverEmail: payload.naverEmail,
                };
            } catch {
                // hint 추출 실패해도 setup 페이지는 빈 폼으로 진입 가능
            }
            navigate('/oauth/setup', { replace: true, state: { pendingToken, hints } });
            return;
        }

        // 기존 유저 로그인
        if (!token) {
            setError('인증 토큰을 받지 못했습니다.');
            return;
        }

        try {
            loginWithToken(token);
            setTimeout(() => navigate('/', { replace: true }), 300);
        } catch (e) {
            setError(e.message || '인증 처리에 실패했습니다.');
        }
    }, [searchParams, loginWithToken, navigate]);

    if (error) {
        return (
            <div className="login-page">
                <div className="login-form-panel" style={{ width: '100%' }}>
                    <div className="login-form-wrapper">
                        <Alert variant="danger">{error}</Alert>
                        <Button variant="primary" onClick={() => navigate('/login')}>
                            로그인 페이지로
                        </Button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="login-page">
            <div className="login-form-panel" style={{ width: '100%' }}>
                <div className="login-form-wrapper" style={{ textAlign: 'center' }}>
                    <Spinner animation="border" />
                    <p style={{ marginTop: 16 }}>로그인 처리 중...</p>
                </div>
            </div>
        </div>
    );
};

export default OAuthCompletePage;
